import { useState } from 'react';
import { useBilling, useBillingMutations } from '../../../hooks/useSettings.js';
import { useInvoices } from '../../../hooks/useInvoices.js';
import { PlanCard } from '../../../components/settings/PlanCard.jsx';
import { InvoiceRow } from '../../../components/settings/InvoiceRow.jsx';
import { SubscriptionStatusPill } from '../../../components/settings/SubscriptionStatusPill.jsx';
import { StripeCardUpdate } from '../../../components/settings/StripeCardUpdate.jsx';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog.jsx';
import { getSelectablePlans, getPlan, planDirection, formatPlanPrice } from '../../../lib/plans.js';
import {
  formatMoney, formatCard, isCardExpiringSoon, formatTrialRemaining,
} from '../../../lib/billing-format.js';
import { formatDate } from '../../../lib/names.js';
import { ApiError } from '../../../lib/api.js';

/**
 * BillingPage — the big one.
 *
 * Sections in order:
 *   1. Subscription summary (current plan, status, trial countdown, usage)
 *   2. Payment method (card on file, update card button)
 *   3. Plan comparison grid with upgrade/downgrade flows
 *   4. Invoice history
 *   5. Cancel subscription (at the bottom, low visual weight)
 *
 * Flow: plan change → preview API call → confirm dialog with prorate
 * amount → API call → success → refetch everything.
 *
 * Payment method update: click button → SetupIntent fetched → Stripe
 * Elements form appears inline → submit → Stripe confirms → webhook
 * fires on backend → refetch billing.
 *
 * Cancel: click button at bottom → confirm dialog explaining immediate
 * effect + pro-rated refund → API call → refetch (status becomes
 * "cancelled", access ends immediately per Jez's rules).
 */

export function BillingPage() {
  const billing = useBilling();
  const invoices = useInvoices();
  const {
    changePlan,
    previewPlanChange,
    cancelSubscription,
    startPaymentMethodUpdate,
  } = useBillingMutations();

  // Plan change flow state
  const [previewingPlan, setPreviewingPlan] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmingPlan, setConfirmingPlan] = useState(false);

  // Card update flow state
  const [cardUpdateOpen, setCardUpdateOpen] = useState(false);
  const [cardUpdateSecret, setCardUpdateSecret] = useState(null);
  const [startingCardUpdate, setStartingCardUpdate] = useState(false);

  // Cancel flow state
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Generic errors
  const [actionError, setActionError] = useState(null);

  if (billing.loading) return <Loading />;
  if (billing.error) return <LoadError onRetry={billing.refresh} />;

  const data = billing.data;
  const currentPlan = getPlan(data.plan);
  const isFounder = data.plan === 'founder';
  const selectablePlans = getSelectablePlans();

  async function handleSelectPlan(planKey) {
    setActionError(null);
    setPreviewingPlan(planKey);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(true);

    try {
      const preview = await previewPlanChange(planKey);
      setPreviewData(preview);
    } catch (err) {
      setPreviewError(err instanceof ApiError ? err.message : 'Could not preview plan change.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleConfirmPlanChange() {
    setConfirmingPlan(true);
    try {
      await changePlan(previewingPlan);
      // Refresh everything — plan, card, invoices
      billing.refresh();
      invoices.refresh();
      setPreviewingPlan(null);
      setPreviewData(null);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not change plan.');
    } finally {
      setConfirmingPlan(false);
    }
  }

  async function handleStartCardUpdate() {
    setActionError(null);
    setStartingCardUpdate(true);
    try {
      const clientSecret = await startPaymentMethodUpdate();
      setCardUpdateSecret(clientSecret);
      setCardUpdateOpen(true);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not start card update.');
    } finally {
      setStartingCardUpdate(false);
    }
  }

  function handleCardUpdateSuccess() {
    setCardUpdateOpen(false);
    setCardUpdateSecret(null);
    billing.refresh();
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      await cancelSubscription();
      billing.refresh();
      invoices.refresh();
      setCancelOpen(false);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not cancel subscription.');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <>
      {actionError && <div className="alert alert-error" role="alert">{actionError}</div>}

      {/* ────────────── Subscription summary ────────────── */}
      <div className="settings-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--sp-3)', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
          <div>
            <h2 className="settings-card-title" style={{ marginBottom: 'var(--sp-1)' }}>
              {currentPlan.name} plan
            </h2>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <SubscriptionStatusPill status={data.status} />
              {data.trial && (
                <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                  {formatTrialRemaining(data.trial)}
                </span>
              )}
            </div>
          </div>
          {!isFounder && !currentPlan.hidden && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 500 }}>
                {formatPlanPrice(currentPlan)}
              </div>
            </div>
          )}
        </div>

        <div className="billing-metrics">
          <div className="billing-metric">
            <div className="billing-metric-label">Active staff</div>
            <div className="billing-metric-value">
              {data.active_staff_count || 0}
              {data.plan_max_staff && (
                <span style={{ color: 'var(--ink-mute)', fontSize: '0.6em', fontWeight: 400 }}>
                  {' '}/ {data.plan_max_staff}
                </span>
              )}
            </div>
            <div className="billing-metric-sub">
              {data.plan_max_staff === null
                ? 'Unlimited on your plan'
                : data.active_staff_count > data.plan_max_staff
                  ? `£2.00 per additional staff`
                  : `${data.plan_max_staff - (data.active_staff_count || 0)} included`}
            </div>
          </div>

          {data.current_period_end && !isFounder && (
            <div className="billing-metric">
              <div className="billing-metric-label">Next invoice</div>
              <div className="billing-metric-value">
                {data.next_invoice ? formatMoney(data.next_invoice.amount_due_pence) : formatPlanPrice(currentPlan)}
              </div>
              <div className="billing-metric-sub">
                Due {formatDate(data.current_period_end)}
              </div>
            </div>
          )}

          {data.cancel_at && (
            <div className="billing-metric">
              <div className="billing-metric-label">Cancelling</div>
              <div className="billing-metric-value" style={{ color: 'var(--id-red-d)' }}>
                {formatDate(data.cancel_at)}
              </div>
              <div className="billing-metric-sub">Access ends on this date</div>
            </div>
          )}
        </div>

        {data.status === 'past_due' && (
          <div className="alert alert-error" role="alert" style={{ marginTop: 'var(--sp-4)' }}>
            <strong>Your last payment failed.</strong> Update your card below
            to keep your verify pages live. Stripe will retry the charge
            automatically over the next 7 days, then suspend access if it
            can't collect.
          </div>
        )}
      </div>

      {/* ────────────── Payment method ────────────── */}
      {!isFounder && (
        <div className="settings-card">
          <h2 className="settings-card-title">Payment method</h2>
          <p className="settings-card-body">
            Used for monthly subscription charges. We never store card
            details — your card is held securely by Stripe.
          </p>

          {data.payment_method ? (
            <div className="payment-method-row">
              <div className="payment-method-icon">
                {(data.payment_method.brand || 'CARD').toUpperCase().slice(0, 4)}
              </div>
              <div className="payment-method-info">
                <div className="payment-method-label">
                  {formatCard(data.payment_method)}
                </div>
                <div className={`payment-method-expiry ${isCardExpiringSoon(data.payment_method) ? 'expiring' : ''}`}>
                  Expires {String(data.payment_method.exp_month).padStart(2, '0')}/{data.payment_method.exp_year}
                  {isCardExpiringSoon(data.payment_method) && ' — expiring soon'}
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-error" role="alert">
              No payment method on file. Add one to keep your subscription active.
            </div>
          )}

          <div className="settings-card-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleStartCardUpdate}
              disabled={startingCardUpdate || cardUpdateOpen}
              aria-busy={startingCardUpdate}
            >
              {startingCardUpdate
                ? (<><span className="spinner" aria-hidden="true" /> Preparing…</>)
                : data.payment_method ? 'Update card' : 'Add card'}
            </button>
          </div>

          {cardUpdateOpen && cardUpdateSecret && (
            <div style={{ marginTop: 'var(--sp-4)', padding: 'var(--sp-4)', background: 'var(--paper)', borderRadius: 'var(--radius)', border: '1px solid var(--line-soft)' }}>
              <StripeCardUpdate
                clientSecret={cardUpdateSecret}
                onSuccess={handleCardUpdateSuccess}
                onCancel={() => { setCardUpdateOpen(false); setCardUpdateSecret(null); }}
              />
            </div>
          )}
        </div>
      )}

      {/* ────────────── Plan comparison ────────────── */}
      {!isFounder && (
        <div className="settings-card" style={{ padding: 'var(--sp-6)' }}>
          <h2 className="settings-card-title">Change plan</h2>
          <p className="settings-card-body">
            Upgrade or downgrade at any time. Changes take effect
            immediately — upgrades charge the pro-rated difference,
            downgrades refund the unused portion.
          </p>

          <div className="plan-grid">
            {selectablePlans.map(plan => (
              <PlanCard
                key={plan.key}
                plan={plan}
                current={plan.key === data.plan}
                popular={plan.popular}
                direction={planDirection(data.plan, plan.key)}
                onSelect={handleSelectPlan}
                selecting={previewingPlan === plan.key && previewLoading}
              />
            ))}
          </div>

          <p className="muted" style={{ fontSize: 'var(--fs-xs)', textAlign: 'center', marginTop: 'var(--sp-3)' }}>
            Prices shown exclude VAT. UK VAT is added at checkout for
            VAT-registered businesses.
          </p>
        </div>
      )}

      {/* ────────────── Invoice history ────────────── */}
      {!isFounder && (
        <div className="settings-card" style={{ padding: 0, overflow: 'hidden' }}>
          <h2 className="settings-card-title" style={{ padding: 'var(--sp-5) var(--sp-5) 0' }}>
            Invoices
          </h2>
          <p className="settings-card-body" style={{ padding: '0 var(--sp-5) var(--sp-4)' }}>
            Past invoices from your TocynID subscription.
          </p>

          {invoices.loading ? (
            <div style={{ padding: 'var(--sp-6)', textAlign: 'center' }}>
              <div className="spinner" aria-hidden="true" />
            </div>
          ) : invoices.error ? (
            <div className="alert alert-error" style={{ margin: 'var(--sp-4)' }}>
              Couldn't load invoices.{' '}
              <button type="button" className="btn btn-ghost" onClick={invoices.refresh} style={{ padding: 0, textDecoration: 'underline' }}>
                Try again
              </button>
            </div>
          ) : invoices.invoices.length === 0 ? (
            <p className="muted" style={{ padding: 'var(--sp-5)', textAlign: 'center' }}>
              No invoices yet. Your first invoice lands when your trial ends.
            </p>
          ) : (
            <>
              <div className="invoice-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Number</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.invoices.map(inv => <InvoiceRow key={inv.id} invoice={inv} />)}
                  </tbody>
                </table>
              </div>
              {invoices.hasMore && (
                <div style={{ padding: 'var(--sp-4)', textAlign: 'center', borderTop: '1px solid var(--line-soft)' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={invoices.loadMore}
                    disabled={invoices.loadingMore}
                  >
                    {invoices.loadingMore ? (
                      <><span className="spinner" aria-hidden="true" /> Loading…</>
                    ) : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ────────────── Cancel subscription ────────────── */}
      {!isFounder && data.status !== 'cancelled' && data.status !== 'canceled' && (
        <div className="settings-card">
          <h2 className="settings-card-title">Cancel subscription</h2>
          <p className="settings-card-body">
            You can cancel any time. Cancellation is effective
            immediately — your verify pages stop working and any
            unused portion of your current billing period is refunded
            pro-rata. Printed QR codes become invalid.
          </p>
          <div className="settings-card-actions">
            <button
              type="button"
              className="btn btn-secondary"
              style={{ color: 'var(--id-red-d)' }}
              onClick={() => setCancelOpen(true)}
            >
              Cancel subscription
            </button>
          </div>
        </div>
      )}

      {/* ────────────── Plan change preview dialog ────────────── */}
      <ConfirmDialog
        open={!!previewingPlan && !previewLoading}
        title={previewData?.amount_refund_pence
          ? `Downgrade to ${getPlan(previewingPlan).name}?`
          : `Upgrade to ${getPlan(previewingPlan).name}?`}
        body={
          previewingPlan && (
            <>
              {previewError ? (
                <div className="alert alert-error" role="alert">
                  {previewError}
                </div>
              ) : previewData ? (
                <>
                  {previewData.amount_due_pence > 0 && (
                    <p>
                      You'll be charged <strong>{formatMoney(previewData.amount_due_pence)}</strong>{' '}
                      today, pro-rated for the remaining{' '}
                      {previewData.days_remaining || 'days'} in this
                      billing period.
                    </p>
                  )}
                  {previewData.amount_refund_pence > 0 && (
                    <p>
                      You'll be refunded{' '}
                      <strong>{formatMoney(previewData.amount_refund_pence)}</strong>{' '}
                      to your card — the unused portion of your{' '}
                      {getPlan(billing.data?.plan).name} plan.
                    </p>
                  )}
                  {previewData.amount_due_pence === 0 && previewData.amount_refund_pence === 0 && (
                    <p>No charge or refund — you'll change to the new plan immediately.</p>
                  )}
                  <div className="preview-summary">
                    <div className="preview-line">
                      <span>New plan</span>
                      <span><strong>{getPlan(previewingPlan).name}</strong></span>
                    </div>
                    <div className="preview-line">
                      <span>Ongoing price</span>
                      <span>{formatPlanPrice(getPlan(previewingPlan))}</span>
                    </div>
                    <div className="preview-line total">
                      <span>Due today</span>
                      <span>
                        {previewData.amount_due_pence > 0
                          ? formatMoney(previewData.amount_due_pence)
                          : previewData.amount_refund_pence > 0
                            ? `-${formatMoney(previewData.amount_refund_pence)} (refund)`
                            : formatMoney(0)}
                      </span>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )
        }
        confirmLabel={
          previewData?.amount_refund_pence > 0 ? 'Downgrade plan' : 'Confirm and pay'
        }
        submitting={confirmingPlan}
        onConfirm={handleConfirmPlanChange}
        onCancel={() => { setPreviewingPlan(null); setPreviewData(null); setPreviewError(null); }}
      />

      {/* ────────────── Cancel confirm dialog ────────────── */}
      <ConfirmDialog
        open={cancelOpen}
        title="Cancel your subscription?"
        body={
          <>
            Your access to TocynID ends <strong>immediately</strong>{' '}
            and your verify pages will return "under review" to
            anyone scanning.{' '}
            <br /><br />
            We'll refund <strong>any unused portion</strong> of your
            current billing period pro-rata to your card in 5-10 working
            days.{' '}
            <br /><br />
            All existing printed QR codes will stop working. You can
            resubscribe any time.
          </>
        }
        confirmLabel="Cancel subscription"
        destructive
        submitting={cancelling}
        onConfirm={handleCancel}
        onCancel={() => setCancelOpen(false)}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────── */

function Loading() {
  return (
    <div style={{ padding: 'var(--sp-10) 0', textAlign: 'center' }}>
      <div className="spinner" aria-hidden="true" />
    </div>
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="alert alert-error" role="alert">
      <strong>Couldn't load billing.</strong>{' '}
      <button type="button" className="btn btn-ghost" onClick={onRetry} style={{ padding: 0, textDecoration: 'underline' }}>
        Try again
      </button>
    </div>
  );
}
